// NaverInsightView.js — 네이버 API 허브(데이터랩) 쇼핑 인사이트를 전체화면으로 보여주는 조회 전용 뷰.
// 호출 경로: 재고 앱 → 자체 프로젝트 Cloud Function(naverShoppingInsight, 키 보호 프록시) → NAVER API HUB.
// 인증 키는 이 화면의 "API 키 설정"에서 저장(관리자 전용, Firestore settings/naver).
import React, { useEffect, useMemo, useRef, useState } from "react";
import { ShoppingBag, X, Search, KeyRound } from "lucide-react";
import Chart from "chart.js/auto";
import { getFunctions, httpsCallable } from "firebase/functions";
import { app, saveNaverSettings, getNaverSettings } from "./firebase";

const callInsight = async (path, body) => {
  const fns = getFunctions(app, "asia-northeast3");
  const res = await httpsCallable(fns, "naverShoppingInsight", { timeout: 60000 })({ path, body });
  return res.data;
};

function ChartBox({ config, height = 220 }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!ref.current || !config) return;
    const c = new Chart(ref.current, config);
    return () => c.destroy();
  }, [config]);
  return <div style={{ height }} className="relative"><canvas ref={ref} /></div>;
}

// 네이버쇼핑 1차 카테고리 코드
const CATEGORIES = [
  { code: "50000008", name: "생활/건강" },
  { code: "50000006", name: "식품" },
  { code: "50000004", name: "가구/인테리어" },
  { code: "50000003", name: "디지털/가전" },
  { code: "50000000", name: "패션의류" },
  { code: "50000001", name: "패션잡화" },
  { code: "50000002", name: "화장품/미용" },
  { code: "50000005", name: "출산/육아" },
  { code: "50000007", name: "스포츠/레저" },
];

const PERIODS = [
  { m: 1, t: "1개월", unit: "date" },
  { m: 3, t: "3개월", unit: "week" },
  { m: 6, t: "6개월", unit: "week" },
  { m: 12, t: "1년", unit: "month" },
];

const PALETTE = ["#4f46e5", "#0ea5e9", "#10b981", "#f59e0b", "#ef4444"];
const ymd = (d) => d.toISOString().slice(0, 10);

// 응답 → 라인차트 config (여러 시리즈 지원 · data 항목에 group이 있으면 group별 분리)
function lineConfig(results) {
  if (!results?.length) return null;
  const datasets = [];
  const labelSet = new Set();
  results.forEach((r) => {
    const grouped = {};
    (r.data || []).forEach((d) => {
      labelSet.add(d.period);
      const g = d.group != null ? String(d.group) : "";
      (grouped[g] = grouped[g] || {})[d.period] = d.ratio;
    });
    Object.entries(grouped).forEach(([g, m]) => {
      datasets.push({ label: g ? `${r.title} · ${g}` : r.title, map: m });
    });
  });
  const labels = [...labelSet].sort();
  return {
    type: "line",
    data: {
      labels,
      datasets: datasets.map((d, i) => ({
        label: d.label,
        data: labels.map((l) => d.map[l] ?? null),
        borderColor: PALETTE[i % PALETTE.length],
        backgroundColor: "transparent",
        tension: 0.3, pointRadius: 1.5, borderWidth: 2, spanGaps: true,
      })),
    },
    options: {
      responsive: true, maintainAspectRatio: false,
      plugins: { legend: { display: true, position: "bottom", labels: { boxWidth: 10, font: { size: 10 } } } },
      scales: { y: { title: { display: true, text: "상대 클릭 비율" } }, x: { ticks: { maxTicksLimit: 12 } } },
    },
  };
}

// group별 합계 → 바차트 config (연령/성별/기기 분포)
function groupBarConfig(results, groupLabels = {}) {
  const sums = {};
  (results || []).forEach((r) => (r.data || []).forEach((d) => {
    const g = String(d.group ?? "");
    sums[g] = (sums[g] || 0) + (Number(d.ratio) || 0);
  }));
  const entries = Object.entries(sums);
  if (!entries.length) return null;
  const totalSum = entries.reduce((s, [, v]) => s + v, 0);
  return {
    type: "bar",
    data: {
      labels: entries.map(([g]) => groupLabels[g] || g),
      datasets: [{ data: entries.map(([, v]) => (totalSum ? (v / totalSum) * 100 : 0)), backgroundColor: "#1e293b", borderRadius: 5, maxBarThickness: 44 }],
    },
    options: {
      responsive: true, maintainAspectRatio: false,
      plugins: { legend: { display: false }, tooltip: { callbacks: { label: (c) => " " + c.raw.toFixed(1) + "%" } } },
      scales: { y: { ticks: { callback: (v) => v + "%" } } },
    },
  };
}

export default function NaverInsightView({ onClose }) {
  const [cat, setCat] = useState(CATEGORIES[0]);
  const [months, setMonths] = useState(3);
  const [keywords, setKeywords] = useState("니트릴장갑, 고무장갑");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [data, setData] = useState(null); // {trend, keyword, age, gender, device}
  const [showKeys, setShowKeys] = useState(false);
  const [kId, setKId] = useState("");
  const [kSecret, setKSecret] = useState("");
  const [keyMsg, setKeyMsg] = useState(null); // {ok, text}

  const openKeys = async () => {
    setShowKeys((v) => !v);
    setKeyMsg(null);
    try {
      const s = await getNaverSettings();
      if (s) { setKId(s.clientId || ""); setKSecret(s.clientSecret || ""); }
    } catch { /* 관리자가 아니면 읽기 불가 — 무시 */ }
  };

  const saveKeys = async () => {
    setKeyMsg(null);
    if (!kId.trim() || !kSecret.trim()) { setKeyMsg({ ok: false, text: "Client ID와 Secret을 모두 입력해주세요." }); return; }
    try {
      await saveNaverSettings(kId, kSecret);
      setKeyMsg({ ok: true, text: "저장되었습니다. '조회'를 눌러 확인해보세요." });
    } catch (e) {
      setKeyMsg({ ok: false, text: (e.message || String(e)).includes("permission") ? "저장 권한이 없습니다 (관리자 계정으로 로그인 필요)." : e.message || String(e) });
    }
  };

  const run = async () => {
    setLoading(true);
    setErr("");
    try {
      const p = PERIODS.find((x) => x.m === months);
      const end = new Date();
      const start = new Date();
      start.setMonth(start.getMonth() - months);
      const base = { startDate: ymd(start), endDate: ymd(end), timeUnit: p.unit };
      const catArr = [{ name: cat.name, param: [cat.code] }];
      const kwList = keywords.split(",").map((s) => s.trim()).filter(Boolean).slice(0, 5);

      const call = callInsight;
      const [trend, age, gender, device, keyword] = await Promise.all([
        call("/shopping/v1/categories", { ...base, category: catArr }),
        call("/shopping/v1/category/age", { ...base, category: cat.code }),
        call("/shopping/v1/category/gender", { ...base, category: cat.code }),
        call("/shopping/v1/category/device", { ...base, category: cat.code }),
        kwList.length
          ? call("/shopping/v1/category/keywords", { ...base, category: cat.code, keyword: kwList.map((k) => ({ name: k, param: [k] })) })
          : Promise.resolve(null),
      ]);
      setData({ trend, age, gender, device, keyword });
    } catch (e) {
      setErr(e.message || String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { run(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const trendCfg = useMemo(() => lineConfig(data?.trend?.results), [data]);
  const keywordCfg = useMemo(() => lineConfig(data?.keyword?.results), [data]);
  const ageCfg = useMemo(() => groupBarConfig(data?.age?.results, { 10: "10대", 20: "20대", 30: "30대", 40: "40대", 50: "50대", 60: "60대+" }), [data]);
  const genderCfg = useMemo(() => groupBarConfig(data?.gender?.results, { m: "남성", f: "여성" }), [data]);
  const deviceCfg = useMemo(() => groupBarConfig(data?.device?.results, { pc: "PC", mo: "모바일" }), [data]);

  const Panel = ({ title, cfg, height, sub }) => (
    <div className="rounded-xl border border-slate-200 bg-white shadow-sm p-4">
      <div className="text-sm font-bold text-slate-700 mb-0.5">{title}</div>
      {sub && <div className="text-[11px] text-slate-400 mb-2">{sub}</div>}
      {cfg ? <ChartBox config={cfg} height={height} /> : <div className="py-10 text-center text-xs text-slate-400">데이터 없음</div>}
    </div>
  );

  return (
    <div className="fixed inset-0 z-[9999] bg-slate-50 overflow-auto">
      <div className="max-w-[1440px] mx-auto p-6">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-y-2">
          <div className="flex items-center gap-2">
            <ShoppingBag className="h-5 w-5 text-emerald-600" />
            <h2 className="text-xl font-bold text-slate-800">쇼핑 인사이트</h2>
            <span className="text-xs text-slate-400 ml-1">네이버 API 허브(데이터랩) — 검색 클릭 추이 기반 시장 트렌드</span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={openKeys} className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-sm font-medium bg-slate-100 text-slate-600 hover:bg-slate-200 transition-all">
              <KeyRound className="h-4 w-4" />API 키 설정
            </button>
            <button onClick={onClose} className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-sm font-medium bg-slate-100 text-slate-600 hover:bg-slate-200 transition-all">
              <X className="h-4 w-4" />닫기
            </button>
          </div>
        </div>

        {/* API 키 설정 (관리자 전용) */}
        {showKeys && (
          <div className="rounded-xl border border-slate-200 bg-white shadow-sm p-4 mb-4">
            <div className="text-sm font-bold text-slate-700 mb-1">네이버 API 허브 인증 정보</div>
            <div className="text-[11px] text-slate-400 mb-3">
              네이버 클라우드 플랫폼 콘솔 → NAVER API HUB → Application 등록(쇼핑 인사이트 선택) → 인증 정보에서 발급. 관리자 계정만 저장할 수 있습니다.
            </div>
            <div className="flex flex-wrap items-end gap-3">
              <div className="flex-1 min-w-[200px]">
                <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-1">Client ID</div>
                <input value={kId} onChange={(e) => setKId(e.target.value)}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-300" />
              </div>
              <div className="flex-1 min-w-[200px]">
                <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-1">Client Secret</div>
                <input type="password" value={kSecret} onChange={(e) => setKSecret(e.target.value)}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-300" />
              </div>
              <button onClick={saveKeys} className="px-4 py-2 rounded-lg text-sm font-medium bg-slate-900 text-white hover:bg-slate-800 transition-all">저장</button>
            </div>
            {keyMsg && <p className={"text-xs mt-2 " + (keyMsg.ok ? "text-emerald-600" : "text-red-600")}>{keyMsg.text}</p>}
          </div>
        )}

        {/* 조회 조건 */}
        <div className="rounded-xl border border-slate-200 bg-white shadow-sm p-4 mb-4 flex flex-wrap items-end gap-3">
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-1">쇼핑 분야</div>
            <select value={cat.code} onChange={(e) => setCat(CATEGORIES.find((c) => c.code === e.target.value))}
              className="rounded-lg border border-slate-200 px-3 py-2 text-sm bg-white">
              {CATEGORIES.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-1">기간</div>
            <div className="flex bg-slate-100 border border-slate-200/60 rounded-lg p-0.5 gap-0.5">
              {PERIODS.map((p) => (
                <button key={p.m} onClick={() => setMonths(p.m)}
                  className={"px-2.5 py-1.5 rounded-md text-xs font-medium transition-all " + (months === p.m ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-800")}>
                  {p.t}
                </button>
              ))}
            </div>
          </div>
          <div className="flex-1 min-w-[240px]">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-1">비교 키워드 (쉼표 구분 · 최대 5개)</div>
            <input value={keywords} onChange={(e) => setKeywords(e.target.value)} placeholder="예: 니트릴장갑, 고무장갑, 수세미"
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-300" />
          </div>
          <button onClick={run} disabled={loading}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium bg-slate-900 text-white hover:bg-slate-800 transition-all disabled:opacity-50">
            <Search className="h-4 w-4" />{loading ? "조회 중…" : "조회"}
          </button>
        </div>

        {err && (
          <div className="rounded-xl border border-red-200 bg-red-50 p-4 mb-4 text-sm text-red-600">
            {err}
            <div className="text-xs text-red-400 mt-1">네이버 API 키가 없다면: 우측 상단 "API 키 설정"에서 Client ID/Secret을 저장하세요.</div>
          </div>
        )}

        {data && !err && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <Panel title={`${cat.name} 분야 클릭 추이`} sub="네이버쇼핑 검색 클릭량의 상대 지수 (최대값=100)" cfg={trendCfg} height={230} />
            <Panel title="키워드별 트렌드 비교" sub="분야 내 키워드 클릭 추이" cfg={keywordCfg} height={230} />
            <Panel title="연령별 분포" sub={`${cat.name} 분야 클릭 비중`} cfg={ageCfg} height={200} />
            <div className="grid grid-cols-2 gap-3">
              <Panel title="성별 분포" cfg={genderCfg} height={200} />
              <Panel title="기기별 분포" cfg={deviceCfg} height={200} />
            </div>
          </div>
        )}
        {loading && !data && <p className="text-slate-500 text-sm py-10 text-center">네이버 데이터랩에서 불러오는 중…</p>}
      </div>
    </div>
  );
}
